import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import closeIcon from '../../assets/icons/close.svg';

/**
 * SearchableSelect - Generic searchable dropdown component
 *
 * Sync mode (default): pass `items` array, component filters client-side.
 * Async mode: pass `onSearch` callback, component calls server on input change.
 *
 * @param {Array} items - Array of items (sync mode only)
 * @param {number|string} selectedItemId - Currently selected item ID
 * @param {Function} onSelectItem - Callback when item is selected (itemId)
 * @param {Function} getItemLabel - Function to get display label for an item
 * @param {Function} getItemDetails - Function to get searchable details (sync mode)
 * @param {Function} filterItem - Custom filter function (sync mode)
 * @param {Function} renderItem - Custom render function for dropdown items (optional)
 * @param {Function} onSearch - Async search function: (term) => items[] (enables async mode)
 * @param {Function} onFetchSelected - Async function to resolve selected item by ID for display
 * @param {string} placeholder - Input placeholder text
 * @param {string} label - Form label text
 * @param {boolean} required - Whether the field is required
 * @param {string} className - Additional CSS classes
 */
export const SearchableSelect = ({
  items = [],
  selectedItemId,
  onSelectItem,
  getItemLabel,
  getItemDetails,
  filterItem,
  renderItem,
  onSearch,
  onFetchSelected,
  placeholder = 'Search...',
  label,
  required = true,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [asyncResults, setAsyncResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const debounceRef = useRef(null);
  const requestCounterRef = useRef(0);
  const isAsync = typeof onSearch === 'function';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('[data-searchable-select]')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Sync mode: update search term when selected item changes externally
  useEffect(() => {
    if (!isAsync) {
      if (selectedItemId) {
        const item = items.find(i => i.id === selectedItemId);
        if (item && getItemLabel) {
          setSearchTerm(getItemLabel(item));
        }
      } else {
        setSearchTerm('');
      }
    }
  }, [selectedItemId, items, getItemLabel, isAsync]);

  // Async mode: resolve selected item for display (edit mode)
  useEffect(() => {
    if (isAsync && selectedItemId && !selectedItem) {
      if (onFetchSelected) {
        onFetchSelected(selectedItemId).then(item => {
          if (item) {
            setSelectedItem(item);
            if (getItemLabel) {
              setSearchTerm(getItemLabel(item));
            }
          }
        });
      }
    } else if (!selectedItemId) {
      setSelectedItem(null);
      if (isAsync) {
        setSearchTerm('');
      }
    }
  }, [selectedItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Async search with debounce
  const doAsyncSearch = useCallback((term) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestCounterRef.current;
      setIsSearching(true);
      try {
        const results = await onSearch(term);
        // Discard stale responses
        if (requestId === requestCounterRef.current) {
          setAsyncResults(results || []);
        }
      } catch (err) {
        console.error('Search error:', err);
        if (requestId === requestCounterRef.current) {
          setAsyncResults([]);
        }
      } finally {
        if (requestId === requestCounterRef.current) {
          setIsSearching(false);
        }
      }
    }, 300);
  }, [onSearch]);

  // Default filter function (sync mode)
  const defaultFilterItem = useCallback((item, searchLower) => {
    if (!getItemDetails) {
      return JSON.stringify(item).toLowerCase().includes(searchLower);
    }
    const details = getItemDetails(item);
    return details.some(detail =>
      String(detail).toLowerCase().includes(searchLower)
    );
  }, [getItemDetails]);

  // Determine display items
  let displayItems;
  if (isAsync) {
    displayItems = asyncResults;
  } else {
    displayItems = items.filter(item => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      if (filterItem) return filterItem(item, searchLower);
      return defaultFilterItem(item, searchLower);
    });
  }

  const handleItemSelect = (itemId) => {
    const source = isAsync ? asyncResults : items;
    const item = source.find(i => i.id === itemId);
    if (item) {
      if (getItemLabel) {
        setSearchTerm(getItemLabel(item));
      }
      if (isAsync) {
        setSelectedItem(item);
      }
    }
    setShowDropdown(false);
    onSelectItem(itemId);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    if (!value) {
      onSelectItem('');
      if (isAsync) {
        setSelectedItem(null);
        setAsyncResults([]);
      }
      return;
    }

    if (isAsync) {
      doAsyncSearch(value);
    }
  };

  const handleFocus = () => {
    setShowDropdown(true);
    // Async mode: load initial results on focus if empty
    if (isAsync && asyncResults.length === 0 && !searchTerm) {
      doAsyncSearch('');
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    onSelectItem('');
    setShowDropdown(false);
    if (isAsync) {
      setSelectedItem(null);
      setAsyncResults([]);
    }
  };

  const isDisabled = !isAsync && items.length === 0;

  return (
    <Form.Group className={`mb-3 ${className}`} style={{ position: 'relative' }} data-searchable-select>
      {label && <Form.Label>{label}{required && ' *'}</Form.Label>}
      <div style={{ position: 'relative' }}>
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          required={required && !selectedItemId}
          autoComplete="off"
          style={{ paddingRight: selectedItemId ? '40px' : '12px' }}
          disabled={isDisabled}
        />
        {selectedItemId && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear selection"
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img src={closeIcon} alt="Clear" style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>

      {/* Dropdown list */}
      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            marginTop: '4px',
          }}
        >
          {isSearching && (
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <Spinner animation="border" size="sm" /> Searching...
            </div>
          )}

          {!isSearching && displayItems.length > 0 && displayItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handleItemSelect(item.id)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: item.id === selectedItemId ? '#e7f3ff' : 'white',
              }}
              onMouseEnter={(e) => {
                if (item.id !== selectedItemId) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (item.id !== selectedItemId) {
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              {renderItem ? renderItem(item) : (getItemLabel ? getItemLabel(item) : `Item ${item.id}`)}
            </div>
          ))}

          {!isSearching && searchTerm && displayItems.length === 0 && (
            <div
              style={{
                padding: '12px',
                color: '#6c757d',
                textAlign: 'center',
              }}
            >
              No items found
            </div>
          )}

          {!isSearching && !searchTerm && !isAsync && displayItems.length === 0 && (
            <div
              style={{
                padding: '12px',
                color: '#6c757d',
                textAlign: 'center',
              }}
            >
              No items found
            </div>
          )}
        </div>
      )}
    </Form.Group>
  );
};

export default SearchableSelect;
